import { BadgeCheckIcon } from "@heroicons/react/solid";
import { find, values } from "lodash";
import { stringifyUrl } from "query-string";
import Tooltip from "rc-tooltip";
import React, { useCallback, useEffect, useMemo } from "react";
import {
  matchPath,
  Redirect,
  RouteComponentProps,
  Switch
} from "react-router-dom";
import CenteredLoading from "../../../components/CenteredLoading";
import DetailsPagination from "../../../components/DetailsPagination";
import HotspotActionsButton from "../../../components/HotspotActionsButton";
import Labels from "../../../components/labels/Labels";
import NavLink from "../../../components/NavLink";
import StatusDot from "../../../components/StatusDot";
import { useComments } from "../../../context/comments";
import { useFilteredList } from "../../../context/filteredList";
import { HotspotMakerAddressKey } from "../../../context/hotspotMakers/HotspotMakersProvider";
import { useHotspots } from "../../../context/hotspots";
import { useInstalls } from "../../../context/installs";
import { useWorkspaces } from "../../../context/workspaces";
import PrivateRoute from "../../../layout/PrivateRoute";
import { avatarsApi, Entity } from "../../../utils/constants";
import { formatHotspotName } from "../../../utils/hotspotUtils";
import { Install } from "../../../utils/types";
import { LabelFilterKey } from "../../../utils/useFilteredLabels";
import useMapCenter from "../../../utils/useMapCenter";
import { useShouldUpdateMapCentroid } from "../../../utils/useShouldCenterMap";
import { isActiveInstall } from "../../Workspace/InstallsData/utils";
import RewardScalingTab from "../common/RewardScalingTab";
import Hotspot5GStatisticsTab from "./Hotspot5GStatistics";
import HotspotCommentsTab from "./HotspotCommentsTab";
import HotspotInfoTab from "./HotspotInfoTab";
import HotspotInstallsTab from "./HotspotInstallsTab";
import HotspotMessagesTab from "./HotspotMessagesTab";
import HotspotOwnerTab from "./HotspotOwnerTab";
import HotspotRewardsTab from "./HotspotRewardsTab";
import HotspotStatusTab from "./HotspotStatusTab";
import HotspotWitnessesTab from "./HotspotWitnessesTab";

const HotspotDetails: React.FC<RouteComponentProps<{ id: string }>> = (
  props
) => {
  const {
    currentHotspot,
    walletVerificationStatusMap,
    workspaceHotspots,
    getHotspotCoordinates
  } = useHotspots();
  const { comments } = useComments();
  const { installs, toggleInstallSidebar } = useInstalls();
  const { currentWorkspaceId } = useWorkspaces();
  const setCenter = useMapCenter((state) => state.setCenter);
  const center = useMapCenter((state) => state.center);
  const boundaries = useMapCenter((state) => state.boundaries);
  const { filteredList } = useFilteredList();
  const filteredHotspots = useMemo(
    () => filteredList[Entity.hotspot],
    [filteredList]
  );

  const installsCount = useMemo(() => {
    if (!currentHotspot) {
      return 0;
    }

    return values(installs).filter(
      (install) =>
        install.hotspot_uuid === props.match.params.id &&
        isActiveInstall(install)
    ).length;
  }, [currentHotspot, installs, props.match.params.id]);

  const commentsCount = useMemo(() => {
    if (!currentHotspot) {
      return 0;
    }

    if (comments[props.match.params.id]) {
      return comments[props.match.params.id].length;
    }
  }, [comments, currentHotspot, props.match.params.id]);

  const hotspotIsInWorkspace = useMemo(
    () =>
      !!find(values(workspaceHotspots), {
        id: props.match.params.id
      }),
    [props.match.params.id, workspaceHotspots]
  );

  const tabs = useMemo(() => {
    const allTabs = [
      { key: "status", title: "Status", count: 0, hidden: false },
      {
        key: "5g-statistics",
        title: "5G Statistics",
        count: 0,
        hidden:
          currentHotspot?.payer !== HotspotMakerAddressKey.bobcat5g &&
          currentHotspot?.payer !== HotspotMakerAddressKey.freedomFi
      },
      { key: "rewards", title: "Rewards", count: 0, hidden: false },
      {
        key: "reward-scaling",
        title: "Reward scaling",
        count: 0,
        hidden: false
      },
      { key: "witnesses", title: "Witnesses", count: 0, hidden: false },
      {
        key: "comments",
        title: "Comments",
        count: commentsCount,
        hidden: false
      },
      {
        key: "installs",
        title: "Installs",
        count: installsCount,
        hidden: !hotspotIsInWorkspace
      },
      {
        key: "owner",
        title: "Contact owner",
        count: currentHotspot?.total_messages || 0,
        hidden:
          !!currentHotspot?.verified_by &&
          !!currentWorkspaceId &&
          currentHotspot.verified_by === currentWorkspaceId
      },
      {
        key: "messages",
        title: "Messages",
        count: currentHotspot?.total_messages || 0,
        hidden: !(
          currentHotspot?.verified_by &&
          currentWorkspaceId &&
          currentHotspot.verified_by === currentWorkspaceId
        )
      },
      { key: "info", title: "Info", count: 0, hidden: false }
    ];

    return allTabs.filter(({ hidden }) => !hidden);
  }, [
    commentsCount,
    currentHotspot?.total_messages,
    currentHotspot?.verified_by,
    currentHotspot?.payer,
    currentWorkspaceId,
    hotspotIsInWorkspace,
    installsCount
  ]);

  const mapCentroid = useShouldUpdateMapCentroid({
    selectedItem: {
      coordinates: [currentHotspot?.lng || 0, currentHotspot?.lat || 0],
      id: currentHotspot?.id || ""
    },
    currentMapCentroid: center,
    mapBoundaries: boundaries
  });

  const canShowRouter = useCallback(
    (key: string) => tabs.find((tab) => tab.key === key)?.hidden === false,
    [tabs]
  );

  const emptyInstall: Install = useMemo(
    () => ({
      uuid: "",
      created_by: "",
      created_at: "",
      updated_by: "",
      updated_at: "",
      hotspot_uuid: currentHotspot?.id ?? "",
      location_uuid: "",
      installed_at: "",
      uninstalled_at: "",
      commissions: []
    }),
    [currentHotspot?.id]
  );

  useEffect(() => {
    setCenter(mapCentroid);
  }, [mapCentroid, setCenter]);

  const detailsPageMatch = matchPath<{
    id?: string;
    section?: string;
    subsection?: string;
  }>(props.location.pathname, {
    path: ["/hotspots/:id/:section/:subsection", "/hotspots/:id/:section"]
  });

  if (!currentHotspot) {
    return <CenteredLoading className="h-full" />;
  }

  return (
    <article className="pb-6">
      <div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="-mt-12 sm:-mt-16">
            <div className="flex justify-between">
              <Tooltip overlay="Click to center the map on this hotspot">
                <img
                  className="h-24 w-24 sm:h-32 sm:w-32 z-10 cursor-pointer"
                  src={stringifyUrl({
                    url: `${avatarsApi}/bottts/${props.match.params.id}.svg`,
                    query: { w: 128, h: 128 }
                  })}
                  onError={(e: any) => {
                    e.target.onerror = null;
                    e.target.src = stringifyUrl({
                      url: `${avatarsApi}/bottts/${props.match.params.id}.svg`,
                      query: { w: 128, h: 128 }
                    });
                  }}
                  alt="Hotspot avatar"
                  onClick={() => {
                    const coordinates = getHotspotCoordinates(
                      props.match.params.id
                    );

                    if (coordinates) {
                      setCenter([coordinates.lng, coordinates.lat]);
                    }
                  }}
                />
              </Tooltip>
              <div className="flex items-center">
                <DetailsPagination
                  path="hotspots"
                  selectedItemUuid={currentHotspot.id}
                  data={filteredHotspots}
                  idKey="id"
                />
              </div>
            </div>
            <div className="sm:flex-1 sm:min-w-0 sm:flex sm:items-center sm:justify-end sm:space-x-6 sm:pb-1">
              <div className="mt-6 min-w-0 flex-1">
                <div className="flex justify-between">
                  <h1 className="text-md sm:text-2xl font-bold text-gray-900 dark:text-white truncate inline-flex items-center">
                    {currentHotspot.name ? (
                      <>
                        {formatHotspotName(currentHotspot)}
                        <StatusDot className="ml-3" hotspot={currentHotspot} />
                      </>
                    ) : (
                      <span className="flex w-20 h-6 animate-pulse bg-gray-400 rounded sm:w-56" />
                    )}

                    {(currentHotspot.verified_by ||
                      !!walletVerificationStatusMap[currentHotspot.owner]) && (
                      <Tooltip
                        placement="top"
                        overlay="The wallet of this hotspot has been verified by a Hotspotty user"
                      >
                        <BadgeCheckIcon className="ml-2 h-6 w-6 text-green-400" />
                      </Tooltip>
                    )}
                  </h1>

                  <HotspotActionsButton
                    hotspot={currentHotspot}
                    toggleAddInstallForm={() =>
                      toggleInstallSidebar(emptyInstall)
                    }
                  />
                </div>

                <Labels
                  className="mt-2"
                  entity={Entity.hotspot}
                  linkedUuid={props.match.params.id}
                  contextWalletAddresses={
                    currentHotspot ? [currentHotspot.owner] : []
                  }
                  filterKey={LabelFilterKey.mapHotspots}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 sm:mt-2 2xl:mt-5">
        <div className="sm:border-b sm:border-gray-200 dark:border-gray-500 overflow-auto whitespace-nowrap">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <select
              className="sm:hidden block w-full pl-3 py-2 sm:text-sm rounded-md dark:bg-gray-800"
              onChange={(e) => {
                props.history.push(`${props.match.url}/${e.target.value}`);
              }}
              value={detailsPageMatch?.params.section}
            >
              {tabs.map(({ key, title }) => (
                <option key={key} value={key}>
                  {title}
                </option>
              ))}
            </select>
            <div className="hidden sm:block">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                {tabs.map(({ key, title, count }) => (
                  <NavLink
                    key={key}
                    to={`${props.match.url}/${key}`}
                    activeClassName="border-hotspotty-400 text-gray-900 dark:text-white dark:text-white dark:border-white"
                    inactiveClassName="border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                    className="whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm"
                  >
                    <span>{title}</span>
                    {!!count && (
                      <span className="bg-gray-200 text-gray-500 group-hover:text-gray-700 dark:text-gray-200 dark:bg-gray-600 dark:group-hover:text-gray-200 dark:group-hover:bg-gray-500 hidden ml-3 py-0.5 px-2.5 rounded-full text-xs font-medium md:inline-block">
                        {count}
                      </span>
                    )}
                  </NavLink>
                ))}
              </nav>
            </div>
          </div>
        </div>
      </div>

      <Switch>
        <PrivateRoute
          path={`${props.match.path}/rewards`}
          component={HotspotRewardsTab as any}
        />
        {canShowRouter("5g-statistics") && (
          <PrivateRoute
            path={`${props.match.path}/5g-statistics`}
            component={Hotspot5GStatisticsTab as any}
          />
        )}
        <PrivateRoute
          path={`${props.match.path}/reward-scaling`}
          component={RewardScalingTab as any}
        />
        <PrivateRoute
          path={`${props.match.path}/witnesses`}
          component={HotspotWitnessesTab as any}
        />
        <PrivateRoute
          path={`${props.match.path}/owner`}
          component={HotspotOwnerTab as any}
        />
        <PrivateRoute
          path={`${props.match.path}/info`}
          component={HotspotInfoTab as any}
        />
        <PrivateRoute
          path={`${props.match.path}/status`}
          component={HotspotStatusTab as any}
        />
        <PrivateRoute
          path={`${props.match.path}/messages`}
          component={HotspotMessagesTab}
        />
        <PrivateRoute
          path={`${props.match.path}/comments`}
          component={HotspotCommentsTab as any}
        />
        <PrivateRoute
          path={`${props.match.path}/installs`}
          component={HotspotInstallsTab as any}
        />

        <Redirect to={`${props.match.path}/status`} />
      </Switch>
    </article>
  );
};

export default HotspotDetails;
